import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import { empresaInicial, lembrarEmpresa } from '../components/empresaLembrada';
import './Programas.css';

/**
 * Programas e Fases (§9.4 do plano de Fases de Atendimento).
 *
 * Programa é TEMPLATE puro: nome, sem datas. As datas vivem na turma, e a janela de cada fase é
 * calculada por membro a partir da âncora da turma. É por isso que esta tela não tem um único
 * campo de data.
 *
 * As fases ficam INLINE aqui, e não numa tela própria, porque a configuração (prompt, meta, régua)
 * mora no seletor de escopo de Configurações — aqui ficam só os campos ESTRUTURAIS da fase, que
 * são poucos.
 *
 * DUAS REGRAS DA API QUE A TELA TEM DE RESPEITAR, senão o CS toma 409 sem entender:
 *  - Gap 11: depois que alguém ENTROU na fase, a estrutura dela (ordem, duração, tipo, regra de
 *    avanço) fica read-only — mudar deslocaria fronteiras de quem já percorreu. O nome segue
 *    editável, e o conteúdo do prompt também (é versionado). A API devolve `walked` por fase.
 *  - §N21: fase que sobrescreve régua de pontuação é OBRIGADA a travar o placar. A tela avisa
 *    antes de tentar desmarcar.
 */

const TIPOS = [
    { key: 'leveling', label: 'Nivelamento' },
    { key: 'behaviors', label: 'Comportamentos' },
];

const emptyPhase = {
    id: null,
    name: '',
    phase_type_key: 'leveling',
    duration_days: '',
    locks_scoreboard: true,
    scores_behaviors: true,
    unlock_after_valid_attendances: '',
    advance_mode: 'duration',
};


// Data pura (YYYY-MM-DD) em dd/mm/aaaa, sem passar por Date (que volta um dia no fuso).
const brDate = (iso) => {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

function Programas({ user }) {
    const [params] = useSearchParams();
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const [companies, setCompanies] = useState([]);
    const [subdomain, setSubdomain] = useState(() => empresaInicial(params.get('subdomain')));
    const [programs, setPrograms] = useState([]);
    const [loading, setLoading] = useState(false);

    const [novoPrograma, setNovoPrograma] = useState('');
    const [editando, setEditando] = useState(null);   // { program_id, phase } — fase em edição
    const [aberto, setAberto] = useState({});          // program_id -> bool

    useEffect(() => {
        (async () => {
            try {
                // promptEditorGetCompanies devolve ARRAY direto (não {companies}) — mesmo
                // consumo do PromptEditor, que é a tela de referência para este endpoint.
                const lista = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(lista) ? lista : []);
                setSubdomain((atual) => atual || (lista.length ? lista[0].subdomain : ''));
            } catch (e) {
                notify('Não foi possível carregar as empresas', 'error');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        lembrarEmpresa(subdomain);
        setLoading(true);
        try {
            const r = await apiService.phaseProgramsList({ subdomain });
            setPrograms(r?.data?.programs || []);
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao carregar programas', 'error');
        } finally {
            setLoading(false);
        }
    }, [subdomain, notify]);

    useEffect(() => { carregar(); }, [carregar]);

    const criarPrograma = async () => {
        const nome = novoPrograma.trim();
        if (!nome) return notify('Dê um nome ao programa', 'error');
        try {
            await apiService.phaseProgramSave({ subdomain, name: nome });
            setNovoPrograma('');
            notify('Programa criado', 'success');
            carregar();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao criar programa', 'error');
        }
    };

    const salvarFase = async (program_id, fase) => {
        const payload = {
            subdomain,
            id: fase.id || undefined,
            program_id,
            name: fase.name?.trim(),
            phase_type_key: fase.phase_type_key,
            duration_days: fase.duration_days === '' ? undefined : Number(fase.duration_days),
            locks_scoreboard: fase.locks_scoreboard,
            scores_behaviors: fase.scores_behaviors,
            // 0 DESLIGA (a API grava NULL). Mandar `undefined` seria "não mexi no campo", e aí
            // esvaziar a caixa na tela não teria como apagar a regra.
            unlock_after_valid_attendances:
                fase.phase_type_key !== 'leveling'
                    ? undefined
                    : (fase.unlock_after_valid_attendances === '' ||
                       fase.unlock_after_valid_attendances === null ||
                       fase.unlock_after_valid_attendances === undefined
                        ? (fase.id ? 0 : undefined)
                        : Number(fase.unlock_after_valid_attendances)),
            advance_mode: fase.advance_mode,
        };
        if (!payload.name) return notify('Dê um nome à fase', 'error');
        if (!fase.id && !payload.duration_days) return notify('Informe a duração em dias', 'error');
        if (fase.id && fase.walked) return salvarFasePercorrida(payload, fase);
        try {
            // Planejado e real andam juntos: mudar duração, ordem ou modo de avanço move as datas
            // FUTURAS de quem já está no programa. A prévia (a API roda tudo e desfaz) mostra quem
            // e o quê antes de gravar — mesma ideia do "Reaplicar plano" da turma.
            const previa = await apiService.phaseSave({ ...payload, dry_run: true });
            const rp = previa?.replan;
            if (rp && rp.vendedores > 0) {
                const turmas = rp.turmas_detalhe || [];
                const ok = await confirmar({
                    titulo: `Salvar "${payload.name}"?`,
                    rotuloConfirmar: 'Salvar e ajustar datas',
                    largo: true,
                    corpo: (
                        <>
                            <div className="dlg-numeros">
                                <div className="dlg-numero"><strong>{rp.vendedores}</strong><span>vendedor(es) com datas ajustadas</span></div>
                                <div className="dlg-numero"><strong>{rp.turmas}</strong><span>turma(s)</span></div>
                            </div>
                            <p>As datas <strong>futuras</strong> mudam para o planejado e o real continuarem iguais:</p>
                            <ul className="dlg-lista">
                                {turmas.slice(0, 12).map((t) => (
                                    <li key={t.cohort_id}>
                                        <strong>{t.name}</strong> ({t.vendedores} vendedor{t.vendedores === 1 ? '' : 'es'}):{' '}
                                        {[...new Set((t.mudancas || []).map((m) => (
                                            m.acao === 'removida' ? `${m.fase} sai da trilha`
                                                : m.acao === 'criada' ? `${m.fase} entra, até ${brDate(m.fim_para)}`
                                                    : `${m.fase} termina ${brDate(m.fim_para)} (era ${brDate(m.fim_de)})`
                                        )))].join(' · ')}
                                    </li>
                                ))}
                                {turmas.length > 12 && <li>… e mais {turmas.length - 12} turma(s)</li>}
                            </ul>
                            <p className="dlg-nota">O que já aconteceu não muda, e fim de fase forçado pelo CS é mantido. A régua passa a mostrar as datas novas.</p>
                        </>
                    ),
                });
                if (!ok) return;
            }
            await apiService.phaseSave(payload);
            setEditando(null);
            notify(fase.id ? 'Fase atualizada' : 'Fase criada', 'success');
            carregar();
        } catch (e) {
            // 409 aqui é regra de negócio, não falha: Gap 11 (fase já percorrida) ou §N21
            // (régua própria exige travar o placar). Mostrar o detalhe da API é o que orienta o CS.
            notify(e?.response?.data?.detail || 'Erro ao salvar fase', 'error');
        }
    };

    // Fase JÁ PERCORRIDA: a duração muda pelo REDIMENSIONAR (a mesma rota que o MCP usa), que
    // reescreve as datas futuras, reclassifica os atendimentos que trocam de fase e realinha o
    // planejado. O resto (nome, travas) vai pelo save comum, SEM campos de estrutura — a guarda da
    // API olha a presença deles, e mandá-los recusaria até uma troca de nome.
    const salvarFasePercorrida = async (payload, fase) => {
        const dur = Number(fase.duration_days);
        const mudouDur = dur && dur !== Number(fase._dur_orig);
        try {
            if (mudouDur) {
                const previa = (await apiService.phaseResize({ subdomain, phase_id: fase.id, duration_days: dur }))?.data;
                const sm = previa?.summary || {};
                const fecha = sm.windows_closed_permanently || 0;
                const ok = await confirmar({
                    titulo: `Redimensionar "${fase.name}" de ${fase._dur_orig} para ${dur} dias?`,
                    rotuloConfirmar: 'Redimensionar',
                    perigo: fecha > 0 || (sm.attendances_moved || 0) > 0,
                    corpo: (
                        <>
                            <div className="dlg-numeros">
                                <div className="dlg-numero"><strong>{sm.members || 0}</strong><span>vendedor(es) em {sm.cohorts || 0} turma(s)</span></div>
                                <div className="dlg-numero"><strong>{sm.attendances_moved || 0}</strong><span>atendimento(s) mudam de fase</span></div>
                            </div>
                            {fecha > 0 && (
                                <p className="dlg-alerta">
                                    <strong>{fecha} janela(s) terminam hoje e não reabrem</strong> — aumentar a duração depois não desfaz.
                                </p>
                            )}
                            <p className="dlg-nota">O que aconteceu antes de hoje não muda. Atendimento que muda de fase passa a valer pela régua da fase nova. A régua passa a mostrar as datas novas.</p>
                        </>
                    ),
                });
                if (!ok) return;
                await apiService.phaseResize({ subdomain, phase_id: fase.id, duration_days: dur, dry_run: false });
            }
            const { duration_days, phase_type_key, advance_mode, ...semEstrutura } = payload;
            await apiService.phaseSave(semEstrutura);
            setEditando(null);
            notify(mudouDur ? 'Fase redimensionada' : 'Fase atualizada', 'success');
            carregar();
        } catch (e) {
            // 403 = redimensionar não liberado para este usuário/ambiente (lista da API).
            notify(e?.response?.data?.detail || 'Erro ao salvar fase', 'error');
        }
    };

    return (
        <div className="programas-page">
            <header className="programas-header">
                <div>
                    <h1>Programas</h1>
                    <p className="programas-sub">
                        Programa é um <strong>template</strong>: nome e fases, sem datas. As datas vêm da
                        turma, e a janela de cada fase é contada por membro a partir da âncora dela.
                    </p>
                </div>
                <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                    {companies.map((c) => (
                        <option key={c.subdomain} value={c.subdomain}>{c.name || c.subdomain}</option>
                    ))}
                </select>
            </header>

            <div className="programas-novo">
                <input
                    value={novoPrograma}
                    onChange={(e) => setNovoPrograma(e.target.value)}
                    placeholder="Nome do novo programa (ex.: Onboarding de Vendedores)"
                    onKeyDown={(e) => e.key === 'Enter' && criarPrograma()}
                />
                <button onClick={criarPrograma}>Criar programa</button>
            </div>

            {loading && <p className="programas-loading">Carregando…</p>}
            {!loading && !programs.length && (
                <p className="programas-vazio">Nenhum programa nesta empresa ainda.</p>
            )}

            {programs.map((p) => {
                const fases = [...(p.phases || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                const emEdicao = editando?.program_id === p.id ? editando.phase : null;
                return (
                    <section key={p.id} className="programa-card">
                        <header onClick={() => setAberto((s) => ({ ...s, [p.id]: !s[p.id] }))}>
                            <span className="programa-nome">{p.name}</span>
                            <span className="programa-meta">
                                {fases.length} {fases.length === 1 ? 'fase' : 'fases'}
                                {fases.length > 0 && ` · ${fases.reduce((t, f) => t + (f.duration_days || 0), 0)} dias`}
                            </span>
                            <span className="programa-toggle">{aberto[p.id] ? '▾' : '▸'}</span>
                        </header>

                        {aberto[p.id] && (
                            <div className="programa-fases">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>#</th><th>Fase</th><th>Tipo</th><th>Duração</th>
                                            <th>Trava o placar</th><th>Comportamento pontua</th>
                                            <th>Destrava feedback</th><th>Avanço</th><th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fases.map((f) => (
                                            <tr key={f.id} className={f.walked ? 'fase-percorrida' : ''}>
                                                <td>{f.sort_order}</td>
                                                <td>{f.name}</td>
                                                <td>{TIPOS.find((t) => t.key === f.phase_type?.key)?.label || f.phase_type?.display_name || '—'}</td>
                                                <td>{f.duration_days ? `${f.duration_days} dias` : '—'}</td>
                                                <td>{f.locks_scoreboard ? 'Sim' : 'Não'}</td>
                                                <td>{f.scores_behaviors === false ? 'Não' : 'Sim'}</td>
                                                <td>{f.unlock_after_valid_attendances
                                                    ? `${f.unlock_after_valid_attendances} atend.`
                                                    : '—'}</td>
                                                <td>{f.advance_mode || 'duration'}</td>
                                                <td>
                                                    <button onClick={() => setEditando({ program_id: p.id, phase: { ...f, phase_type_key: f.phase_type?.key, _dur_orig: f.duration_days } })}>
                                                        Editar
                                                    </button>
                                                    {f.walked && (
                                                        <span className="fase-tag" title="Alguém já entrou nesta fase: ordem, duração, tipo e regra de avanço ficam travados para não deslocar quem já percorreu.">
                                                            já percorrida
                                                        </span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                        {!fases.length && (
                                            <tr><td colSpan="7" className="fases-vazio">Programa sem fases. Crie a primeira abaixo.</td></tr>
                                        )}
                                    </tbody>
                                </table>

                                {!emEdicao && (
                                    <button className="btn-nova-fase"
                                        onClick={() => setEditando({ program_id: p.id, phase: { ...emptyPhase } })}>
                                        + Nova fase
                                    </button>
                                )}

                                {emEdicao && (
                                    <div className="fase-form">
                                        <h4>{emEdicao.id ? `Editar fase: ${emEdicao.name}` : 'Nova fase'}</h4>
                                        {emEdicao.walked && (
                                            <p className="fase-aviso">
                                                Alguém já entrou nesta fase. Tipo e regra de avanço ficam travados. A
                                                <strong> duração</strong> pode mudar: salvar mostra antes quantos vendedores e
                                                atendimentos são afetados, e o que aconteceu antes de hoje não muda.
                                            </p>
                                        )}
                                        <label>Nome
                                            <input value={emEdicao.name}
                                                onChange={(e) => setEditando((s) => ({ ...s, phase: { ...s.phase, name: e.target.value } }))} />
                                        </label>
                                        <label>Tipo
                                            <select value={emEdicao.phase_type_key} disabled={!!emEdicao.walked}
                                                onChange={(e) => setEditando((s) => ({ ...s, phase: { ...s.phase, phase_type_key: e.target.value } }))}>
                                                {TIPOS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                                            </select>
                                        </label>
                                        <label>Duração (dias)
                                            <input type="number" min="1" value={emEdicao.duration_days ?? ''}
                                                onChange={(e) => setEditando((s) => ({ ...s, phase: { ...s.phase, duration_days: e.target.value } }))} />
                                        </label>
                                        <label className="fase-check">
                                            <input type="checkbox" checked={!!emEdicao.locks_scoreboard}
                                                onChange={(e) => setEditando((s) => ({ ...s, phase: { ...s.phase, locks_scoreboard: e.target.checked } }))} />
                                            Travar o placar nesta fase
                                        </label>
                                        <p className="fase-hint">
                                            Travar fecha o placar individual no app e tira o vendedor do ranking enquanto
                                            ele estiver nesta fase. É obrigatório se a fase tiver régua de pontuação
                                            própria — pontos de réguas diferentes não podem competir no mesmo ranking.
                                        </p>

                                        <label className="fase-check">
                                            <input type="checkbox" checked={emEdicao.scores_behaviors !== false}
                                                onChange={(e) => setEditando((s) => ({ ...s, phase: { ...s.phase, scores_behaviors: e.target.checked } }))} />
                                            Comportamento pontua nesta fase
                                        </label>
                                        <p className="fase-hint">
                                            Desmarque para que os comportamentos <strong>não valham ponto</strong> enquanto o
                                            vendedor estiver nesta fase — útil quando a fase mede outra coisa (nivelamento,
                                            por exemplo) e os pontos de comportamento atrapalhariam a leitura.
                                            <br />
                                            A avaliação <strong>não muda</strong>: a IA continua avaliando os comportamentos e
                                            eles continuam aparecendo na revisão do atendimento — só valem 0. É a alternativa
                                            a copiar o prompt de avaliação inteiro para a fase só para desligar as flags de
                                            pontos, cópia que depois diverge do global a cada melhoria.
                                            <br />
                                            <strong>Vale só para os atendimentos novos.</strong> O que já foi avaliado
                                            mantém os pontos que valeram — a regra é congelada em cada atendimento no
                                            momento em que ele é processado.
                                        </p>
                                        {emEdicao.phase_type_key === 'leveling' && (
                                            <>
                                                <label>
                                                    Destravar feedback após (atendimentos válidos)
                                                    <input type="number" min="0"
                                                        value={emEdicao.unlock_after_valid_attendances ?? ''}
                                                        placeholder="vazio = sem regra"
                                                        onChange={(e) => setEditando((s) => ({ ...s, phase: { ...s.phase, unlock_after_valid_attendances: e.target.value } }))} />
                                                </label>
                                                <p className="fase-hint">
                                                    Enquanto o vendedor não gravar essa quantidade de atendimentos
                                                    <strong> válidos nesta fase</strong>, ele não vê feedback nem histórico no
                                                    app. Ao atingir, destrava sozinho e a <strong>data fica registrada</strong>.
                                                    Vazio = sem regra, que é o comportamento de hoje.
                                                    <br />
                                                    Ele passa a ver <strong>a partir do dia em que destravou</strong> — os
                                                    atendimentos anteriores continuam fora. E isto <strong>não mexe no
                                                    placar nem no ranking</strong>: quem cuida disso é "travar o placar".
                                                    <br />
                                                    Só existe em fase de <strong>Nivelamento</strong>. Mudar o número depois
                                                    não afeta quem já destravou.
                                                </p>
                                            </>
                                        )}
                                        <div className="fase-acoes">
                                            <button onClick={() => salvarFase(p.id, emEdicao)}>Salvar</button>
                                            <button className="btn-cancelar" onClick={() => setEditando(null)}>Cancelar</button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                );
            })}

            {toast && <div className={`programas-toast ${toast.type}`}>{toast.msg}</div>}
        </div>
    );
}

export default Programas;
